// DeleteDatabaseDialog — admin-only. Permanently DROP a database. Destructive
// (runs DROP DATABASE), so the Delete button is armed only once the user types
// the exact database name. Reuses the restore dialog's styling.
//
// Props:
//   connectionId  number
//   database      string   — the database to drop
//   openTabs      string[] — labels of currently-open inner tabs bound to this DB
//                            (shown as an extra warning; they'll break after drop)
//   onDeleted(db) fn()     — called after a successful drop (refresh the sidebar)
//   onClose       fn()
import { createSignal, Show, For } from 'solid-js';
import Modal from './Modal.jsx';
import { dropDatabase } from '../lib/dbadmin.js';
import { toast } from '../lib/toast.js';

export default function DeleteDatabaseDialog(props) {
  const [confirmText, setConfirmText] = createSignal('');
  const [busy, setBusy] = createSignal(false);
  const canDelete = () => confirmText() === props.database && !busy();

  const doDelete = async () => {
    if (!canDelete()) return;
    setBusy(true);
    try {
      await dropDatabase(props.connectionId, props.database);
      toast.success(`Database “${props.database}” dropped`);
      props.onDeleted?.(props.database);
      props.onClose();
    } catch (e) {
      toast.error('Drop failed: ' + (e?.message || e));
      setBusy(false);
    }
  };

  return (
    <Modal open={true} title="Delete database" onClose={props.onClose}>
      <div class="restore-dialog">
        <div class="restore-warning">
          ⚠ This permanently runs <span class="mono">DROP DATABASE</span> on{' '}
          <span class="mono">{props.database}</span> — every table, view, routine, and row
          inside it is destroyed. This cannot be undone.
        </div>

        <Show when={props.openTabs && props.openTabs.length > 0}>
          <div class="restore-warning">
            ⚠ {props.openTabs.length} open tab{props.openTabs.length === 1 ? '' : 's'} target
            this database and will stop working after it's dropped:
            <ul style="margin:6px 0 0;padding-left:18px;">
              <For each={props.openTabs}>{(label) => <li class="mono">{label}</li>}</For>
            </ul>
          </div>
        </Show>

        <label class="restore-field">
          <span class="restore-field-label">
            Type <span class="mono restore-confirm-name">{props.database}</span> to confirm
          </span>
          <input
            class="input"
            placeholder="database name"
            value={confirmText()}
            disabled={busy()}
            onInput={(e) => setConfirmText(e.currentTarget.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && canDelete()) doDelete(); }}
            autofocus
          />
        </label>

        <div class="restore-actions">
          <button class="btn btn-ghost" onClick={props.onClose} disabled={busy()}>Cancel</button>
          <button class="btn btn-danger" onClick={doDelete} disabled={!canDelete()}>
            {busy() ? 'Dropping…' : '🗑 Delete database'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
